import React, { useEffect, useRef, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import moment from 'moment';
import clsx from 'clsx';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { patient, savePatientInWardData } from '../../../store/reducers/patient';
import { DNACPROrderStatus } from '../../ward/forms/dnacpr/dnacpr_data';
import MessageBox from '../../../util/messageBox';
import {
    additionalData,
    loadPatientAdditionalData,
    loadPatientHistoricalData,
    updatePatientAdditionalData,
} from '../../../store/reducers/additionalData';
import { clearWorkflow } from '../../../store/reducers/workflow';
import API from '../../../app/api';
import { hasPermission, Permissions, sendUserAudit, user } from '../../../store/reducers/user';
import useOpenState from '../../../hooks/useOpenState';
import { agyleBlockContext, setOpenAlarms, setSnackbar } from '../../../store/reducers/globals';
import AgyleHeader from '../../../components/header/agyleHeader/agyleHeader';
import PatientDemographicWarning from '../../../components/patient/patientDemographicWarning';
import PatientContextHeader from './components/patientContextHeader';
import {
    changePatientContextTab,
    checkPatientWorkflow,
    ctxBlockExitForm,
    ctxCurrentTab,
    ctxFormIsOpen,
    ctxFullFormOpen,
    ctxFullFormsQueue,
    ctxHistoryPatient,
    ctxIsReAttendance,
    ctxOpenWarningFormOpen,
    ctxPatientAgeType,
    ctxPatientLocked,
    ctxPatientSpecialTags,
    ctxPatientType,
    ctxReferralFormData,
    ctxShowDischargeTemplates,
    ctxSplitScreen,
    ctxTempCurrentTab,
    ctxValueToModify,
    goToBaseTab,
    modifyValuesInPatientInWard,
    patientContextClearStates,
    setAgeUnknown,
    setClinicalConclusionNeeded,
    setContextSplitScreen,
    setCtxCurrentPatientInWard,
    setCurrentTab,
    setFullFormOpen,
    setFullFormsQueue,
    setHistoryPatient,
    setIsReAttendance,
    setObservationType,
    setOpenWarningFormOpen,
    setPatientAgeType,
    setPatientBannerType,
    setPatientObsSettings,
    setPatientPastActivity,
    setPatientSpecialTags,
    setPatientType,
    setReferralFormData,
    setShowDischargeTemplates,
    setTempCurrentTab,
    setValueToModify,
} from '../../../store/reducers/patientContext';
import { BASE_TAB, getPatientEDContextType, getTabHeight, PATIENT_AGE_TYPE, PATIENT_CONTEXT_TYPE } from '../../../util/patientContextUtil';
import PatientContextAlert from './components/patientContextAlert';
import ContextTabs from './contextTabs';
import ContextBox from './contextBox';
import {
    isClinicalConclusionNeeded,
    mapReferralDataToForm,
    PATIENT_BANNER_TYPE,
    PATIENT_CONTEXT_TABS_ID,
} from '../../ward/forms/formsUtil';
import { getConfig } from '../../../util/configUtil';
import PatientContextWarningDialog from './components/patientContextWarningDialog';
import PatientContextClinicalConclusionNeeded from './components/patientContextClinicalConclusionNeeded';
import ContextFormContainer from './components/forms/contextFormContainer';
import FullFormContainer from './components/forms/fullFormContainer';
import { DISCHARGE_TYPE, getAdmissionStartDate, PATIENT_TAGS } from '../../../util/patientUtil';
import { getECS } from '../../ward/forms/ecs/ecs';
import { addNoteForPatient } from '../../../store/reducers/notes';
import { obsAges, obsTypes } from '../../ward/forms/observation/obsUtils';
import { getPatientFlagValue } from '../../ward/mainScreen/util/patientFlagsUtil';
import { setCurrentContextType } from '../../../store/reducers/globalModal';
import BarcodeModal from './components/barcodeModal';
import { GLOBAL_PANEL_TYPE } from '../../../components/actionPanel/components/panelType';
import { useHistory, useLocation } from 'react-router-dom';
import useLockPatient from '../../../hooks/ws/useLockPatient';
import useWorkflow from '../../../hooks/useWorkflow';
import { setFacility } from '../../../store/reducers/appointments';
import PatientContextReferralModal from './components/patientContextReferralModal';
import { AUDIT_TYPES } from '../../../util/auditUtil';
import AlertUpdatePatientData from '../../../components/modal/alertUpdatePatientData';

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
        backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#222425',
    },
    dialogRoot: {
        backgroundColor: theme.palette.background.paperDarker,
        padding: '0 !important',
        overflowY: 'hidden',
        height: 'auto',
        paddingBottom: 0,
    },
    pastAttendanceView: {
        border: '10px solid #FFC400',
    },
    isLockedView: {
        border: '10px solid #e85e5e',
    },
    reAttendanceView: {
        border: '10px solid #B3D4FF',
    },
    splitScreen: {
        display: 'flex',
        alignItems: 'stretch',
        gap: 1,
    },
    splitBorder: {
        border: '2px solid #444444',
        '&.active': {
            // borderColor: '#888888'
            borderColor: theme.palette.primary.main,
        },
    },
    headerBackground: {
        backgroundColor: `${theme.palette.background.paperDarker} !important`,
    },
}));

function ContextViewer({
    currentPatient,
    disableSave,
    idElement,
    isSameName,
    pastAttendances,
    contextOpen,
    closeContext,
    loadHist,
    formPatientSearch = false,
    saveAttendanceDataById = false,
    fromPatientContext = false,
    disableBack = false,
}) {
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const match = useLocation();

    useWorkflow(currentPatient);

    // Lock patient service
    const [isPatientLock, isLockByMe] = useLockPatient(currentPatient?.patient?.__id);

    // Config var
    const isUHDClient = getConfig('client', null) === 'UHD';
    const patientReminder = getConfig('patientReminder', false);
    const similarNameWarningBox = getConfig('similarNameWarningBox', false);

    // Selectors
    const userObject = useSelector(user);
    const historyPatient = useSelector(ctxHistoryPatient);
    const currentTab = useSelector(ctxCurrentTab);
    const formIsOpen = useSelector(ctxFormIsOpen);
    const isFullFormOpen = useSelector(ctxFullFormOpen);
    const isLocked = useSelector(ctxPatientLocked);
    const patientAddData = useSelector(additionalData);
    const patientObjectSelectorObj = useSelector(patient);
    const blockContext = useSelector(agyleBlockContext);
    const valModifyPatient = useSelector(ctxValueToModify);
    const openWarningFormOpen = useSelector(ctxOpenWarningFormOpen);
    const tempCurrentTab = useSelector(ctxTempCurrentTab);
    const splitScreen = useSelector(ctxSplitScreen);
    const patientType = useSelector(ctxPatientType);
    const patientAgeType = useSelector(ctxPatientAgeType);
    const patientTags = useSelector(ctxPatientSpecialTags);
    const formQueue = useSelector(ctxFullFormsQueue);
    const isBlockExitForm = useSelector(ctxBlockExitForm);
    const referralFormData = useSelector(ctxReferralFormData);
    const isShowDischargeTemplates = useSelector(ctxShowDischargeTemplates);
    const isReAttendance = useSelector(ctxIsReAttendance);

    const isContextDisabled = disableSave || isLocked;

    // Patient states
    const [currentPatientInWard, setCurrentPatientInWard] = useState(null);
    const [patientObject, setPatientObject] = useState(null);
    const [patientIsDischarged, setPatientIsDischarged] = useState(false);
    const [patientIsDischargedEdeu, setPatientIsDischargedEdeu] = useState(false);
    const mainPastActivity = patientIsDischarged || patientIsDischargedEdeu || pastAttendances;

    // Patient Reminder Modal
    const [isPatientReminderOpen, openPatientReminder, closePatientReminder] = useOpenState(false, true);
    const tabFocus = useRef(false);

    // Open states
    const [confirmCloseModal, setVisibleCloseModal] = useState(false);
    const [isWarningBoxSimilarNameOpen, openWarningBoxSimilarName, closeWarningBoxSimilarName] = useOpenState(isSameName || false, true);

    const closeContextChoose = useRef(false);
    const containerRef = useRef({ one: null, two: null });

    useEffect(() => {
        dispatch(setObservationType('obs'));
    }, []);

    // Open alert for similar name
    useEffect(() => {
        if (isSameName) openWarningBoxSimilarName();
    }, [isSameName]);

    useEffect(() => {
        if (_.isEmpty(formQueue)) dispatch(setFullFormOpen(false));
    }, [formQueue]);

    useEffect(() => {
        if (referralFormData && patientType === PATIENT_CONTEXT_TYPE.REVIEW) {
            dispatch(setReferralFormData(null));
        }
    }, [patientType, referralFormData]);

    useEffect(() => {
        if (currentPatient?.metadata?.snReferralId && !referralFormData) {
            API.getReferralReview(currentPatient.metadata.snReferralId)
                .then((data) => {
                    dispatch(setReferralFormData(mapReferralDataToForm(data)));
                })
                .catch((error) => {
                    console.error(error);
                    dispatch(setSnackbar({ type: 'error', message: t('Error loading referral data.') }));
                });
        }
    }, [currentPatient?.metadata?.snReferralId, referralFormData]);

    useEffect(() => {
        // Patients of unknown age, set to 18 years of age
        const ptObject = _.cloneDeep(historyPatient ?? currentPatient);

        if (!isShowDischargeTemplates && ptObject?.facility) {
            dispatch(setFacility(ptObject?.facility));
        }

        if (currentPatient && !currentPatient?.patient?.dob) {
            ptObject.patient.dob = moment().subtract(18, 'year').subtract(1, 'day').toISOString();
            setCurrentPatientInWard(ptObject);
            dispatch(setAgeUnknown(true));
        } else {
            dispatch(setAgeUnknown(false));
            setCurrentPatientInWard(ptObject);
        }

        if (ptObject) {
            let isReAttendance = false;
            if (ptObject?.admission?.attendanceId) {
                const { attendanceId } = ptObject.admission;
                isReAttendance = attendanceId.match(/^.*-O\d{2}$/) !== null;
            }

            dispatch(setCtxCurrentPatientInWard(ptObject));
            const age = moment.duration(getAdmissionStartDate(ptObject?.admission).diff(moment(ptObject?.patient?.dob))).asYears();
            let type = PATIENT_AGE_TYPE.ADULT;
            if (age >= 75) {
                type = PATIENT_AGE_TYPE.OVER75;
            }
            if (age < 18) {
                type = PATIENT_AGE_TYPE.PAEDS;
            }
            dispatch(setPatientAgeType(type));

            // Load Observation settings
            let newType;
            let ageRange = null;
            let neuroAgeRange;
            if (age >= 16) {
                newType = obsTypes.news;
            } else {
                newType = obsTypes.pews;
                if (age < 1) {
                    ageRange = obsAges._0_1;
                } else if (age >= 1 && age < 5) {
                    ageRange = obsAges._1_5;
                } else if (age >= 5 && age < 12) {
                    ageRange = obsAges._5_12;
                } else {
                    ageRange = obsAges._12_18;
                }
            }
            // AGL-737 - 001. Recording Neurological Observations
            if (age < 5) {
                neuroAgeRange = obsAges._1_5;
            } else if (age >= 5 && age < 16) {
                neuroAgeRange = obsAges._5_16;
            } else {
                neuroAgeRange = obsAges._16_99;
            }
            dispatch(
                setPatientObsSettings({
                    obsType: newType,
                    obsAgeRange: ageRange,
                    neuroAgeRange: neuroAgeRange,
                }),
            );
            dispatch(setIsReAttendance(isReAttendance));
        }

        if (!currentPatientInWard && ptObject?.patient) {
            dispatch(loadPatientAdditionalData(ptObject.patient.__id));
            dispatch(loadPatientHistoricalData(ptObject));
        }

        setPatientIsDischarged(
            !!ptObject?.admission?.endDate ||
                ptObject?.admission?.status === 'DISCHARGE_REQUESTED' ||
                ptObject?.admission?.status === 'TRANSFER_REQUESTED',
        );
        setPatientIsDischargedEdeu(
            !!ptObject?.admission?.endDate || ptObject?.metadata?.EDAUDischarged ? ptObject?.metadata?.EDAUDischarged : false,
        );

        const isPatientInEDAU = ptObject?.wardId === '0EDAU';
        const isPatientIP = ptObject?.admission?.attendanceType === 'I' || ptObject?.admission?.attendanceType === 'IP';

        dispatch(setPatientSpecialTags(ptObject?.patient?.tags ?? []));

        // Set patient context type
        if (formPatientSearch || !ptObject?.__id) {
            dispatch(setPatientType(PATIENT_CONTEXT_TYPE.REVIEW));
        } else if (isPatientInEDAU) {
            dispatch(setPatientType(PATIENT_CONTEXT_TYPE.EDAU));
        } else if (isPatientIP) {
            dispatch(setPatientType(PATIENT_CONTEXT_TYPE.INPATIENT));
        } else {
            dispatch(setPatientType(getPatientEDContextType(ptObject?.admission?.pointOfCare)));
        }

        if (!pastAttendances && !isLocked && !historyPatient)
            dispatch(checkPatientWorkflow(currentPatientInWard, ptObject, isPatientIP, patientType));
    }, [historyPatient, currentPatient, patientType]);

    useEffect(() => {
        dispatch(setPatientPastActivity(patientIsDischarged || patientIsDischargedEdeu || pastAttendances || historyPatient));
    }, [patientIsDischarged, patientIsDischargedEdeu, pastAttendances, historyPatient]);

    useEffect(() => {
        if (patientReminder) {
            let hidden, visibilityChange;
            if (typeof document.hidden !== 'undefined') {
                // Opera 12.10 and Firefox 18 and later support
                hidden = 'hidden';
                visibilityChange = 'visibilitychange';
            } else if (typeof document.msHidden !== 'undefined') {
                hidden = 'msHidden';
                visibilityChange = 'msvisibilitychange';
            } else if (typeof document.webkitHidden !== 'undefined') {
                hidden = 'webkitHidden';
                visibilityChange = 'webkitvisibilitychange';
            }

            function handleVisibilityChange() {
                if (!document[hidden]) {
                    openPatientReminder();
                }
            }

            if (contextOpen) {
                // Show popup about selected patient
                document.addEventListener(visibilityChange, handleVisibilityChange, false);
            }
            return () => {
                document.removeEventListener(visibilityChange, handleVisibilityChange, false);
            };
        }
    }, [contextOpen]);

    useEffect(() => {
        const deceased = getPatientFlagValue(historyPatient ?? currentPatient, 'deceased');
        if (deceased) {
            dispatch(setPatientBannerType(PATIENT_BANNER_TYPE.DECEASED));
        } else if (patientAddData?.other?.dncpr) {
            let activeDncpr = _.find(patientAddData.other.dncpr, ['status', DNACPROrderStatus.ACTIVE]);
            let awaitingDncpr = _.find(patientAddData.other.dncpr, ['status', DNACPROrderStatus.AVAITING]);

            if (activeDncpr && moment().isBefore(moment(activeDncpr.validUntilDate))) {
                dispatch(setPatientBannerType(PATIENT_BANNER_TYPE.ACTIVE_DNCPR));
            } else if (awaitingDncpr && moment().isBefore(moment(awaitingDncpr.validUntilDate))) {
                dispatch(setPatientBannerType(PATIENT_BANNER_TYPE.AWAITING_DNCPR));
            } else {
                dispatch(setPatientBannerType(PATIENT_BANNER_TYPE.UNSET));
            }
        } else {
            dispatch(setPatientBannerType(PATIENT_BANNER_TYPE.UNSET));
        }
    }, [historyPatient, currentPatient, patientAddData]);

    useEffect(() => {
        const controller = new AbortController();
        if (pastAttendances && !isLocked && !historyPatient && currentPatientInWard && !isUHDClient) {
            API.requestForIceReports(currentPatientInWard?.patient?.__id, controller.signal);
        }
        return () => {
            controller.abort();
        };
    }, [currentPatientInWard]);

    useEffect(() => {
        setPatientObject(patientObjectSelectorObj);
    }, [patientObjectSelectorObj]);

    useEffect(() => {
        if (patientAddData?.other?.alarms) {
            const active = _.filter(
                patientAddData?.other?.alarms,
                (alarm) => !alarm.deleted && moment().isBefore(moment(alarm.expireDate)),
            );
            let check = true;
            _.forEach(active, (alarm) => {
                if (!check) return;
                const find = _.find(alarm?.acknowledgeBy, { user: userObject?.userName });
                check = !!find;
            });
            if (!check) dispatch(setOpenAlarms(true));
        }
    }, [patientAddData]);

    useEffect(() => {
        if (document.getElementById('tabContent'))
            document.getElementById('tabContent').style.height = window.innerHeight - getTabHeight(idElement).height + 'px';

        dispatch(
            setCurrentContextType(
                currentTab === PATIENT_CONTEXT_TABS_ID.OBSERVATIONS ? GLOBAL_PANEL_TYPE.OBSERVATIONS : GLOBAL_PANEL_TYPE.PATIENT,
            ),
        );
    }, [currentTab, patientAddData, idElement]);

    /**
     * Effect to update the current context type based on the active tab in split screen mode.
     * If split screen is enabled, it sets the context type to either OBSERVATIONS or PATIENT
     * based on the active screen's tab.
     */
    useEffect(() => {
        if (splitScreen?.enabled) {
            const activeTab = splitScreen.activeScreen === 1 ? splitScreen.screenOne : splitScreen.screenTwo;
            dispatch(
                setCurrentContextType(
                    activeTab === PATIENT_CONTEXT_TABS_ID.OBSERVATIONS ? GLOBAL_PANEL_TYPE.OBSERVATIONS : GLOBAL_PANEL_TYPE.PATIENT,
                ),
            );
        }
    }, [splitScreen]);

    useEffect(() => {
        if (valModifyPatient && _.isArray(valModifyPatient) && !_.isEmpty(valModifyPatient)) {
            modifyValues(valModifyPatient);
            dispatch(setValueToModify(null));
        }
    }, [valModifyPatient, currentPatientInWard]);

    const closeDialog = (force) => {
        if (disableBack) return;
        closeContextChoose.current = false;
        const isTemporary = patientTags?.includes(PATIENT_TAGS.TEMPORARY);

        const clinicalConclusionNeeded =
            dispatch(hasPermission(Permissions.AGYLE_CAN_MANAGE_CLERKING)) && isClinicalConclusionNeeded(currentPatientInWard);
        if (currentTab === PATIENT_CONTEXT_TABS_ID.NOTE_DOCUMENT) {
            dispatch(setCurrentTab(PATIENT_CONTEXT_TABS_ID.NOTES));
        } else if (blockContext) {
            dispatch(
                setSnackbar({
                    type: 'warning',
                    message: t('Close the current sepsis pathway to perform this operation.'),
                }),
            );
        } else if (!force && clinicalConclusionNeeded && !pastAttendances && !historyPatient && !isTemporary) {
            dispatch(
                setClinicalConclusionNeeded({
                    open: true,
                    fromDischarge: false,
                }),
            );
        } else if (isBlockExitForm) {
            dispatch(setSnackbar({ type: 'warning', message: 'Save the form to exit patient context' }));
        } else if (isFullFormOpen && !force) {
            closeContextChoose.current = true;
            dispatch(setOpenWarningFormOpen(true));
        } else if (historyPatient) {
            dispatch(setCurrentTab(BASE_TAB));
            dispatch(setHistoryPatient(null));
        } else {
            setVisibleCloseModal(false);
            dispatch(patientContextClearStates());
            setCurrentPatientInWard(null);
            dispatch(setCtxCurrentPatientInWard(null));
            dispatch(setFullFormsQueue([]));
            dispatch(setFullFormOpen(false));
            handleCloseWarning();

            if (loadHist) loadHist();

            if (fromPatientContext) {
                history.goBack();
            } else if (closeContext) {
                closeContext();
            }
            dispatch(goToBaseTab());
        }
    };

    const saveValues = (values, isDischarge = false) => {
        dispatch(
            savePatientInWardData(
                values,
                currentPatientInWard,
                patientAddData,
                patientType,
                isDischarge ? DISCHARGE_TYPE.ED_DISCHARGE : DISCHARGE_TYPE.NONE,
                currentPatientInWard?.admission?.facility || null,
                pastAttendances || historyPatient,
                saveAttendanceDataById || fromPatientContext,
                (updateVals) => {
                    if (updateVals) modifyValues(updateVals);
                    if (isDischarge) {
                        closeContext();
                        if (isShowDischargeTemplates) {
                            history.push('/scheduler');
                            dispatch(setShowDischargeTemplates(false)); // Appointments - status change to Departed
                        }
                    }
                    if (loadHist) {
                        loadHist();
                    }
                },
            ),
        );
        dispatch(loadPatientHistoricalData(currentPatientInWard));
    };

    const savePatientDataValues = (value) => {
        const { form, note, path } = value;
        const listPath = _.get(patientAddData, `other.${path}`);
        let array = _.cloneDeep(listPath || []);
        const index = _.findIndex(array, { __id: form.__id });
        if (index !== -1) {
            array.splice(index, 1, form);
        } else {
            array.push(form);
        }

        const data = {
            [path]: array,
        };

        dispatch(updatePatientAdditionalData(currentPatientInWard.patient.__id, data));
        dispatch(
            addNoteForPatient(
                currentPatientInWard.patient.__id,
                {
                    note: note,
                    noteType: path,
                    patientsInWardsRef: [currentPatientInWard.__id],
                    referenceId: form.__id,
                    episodeId: currentPatientInWard?.admission?.consultantEpisodeId || null,
                },
                currentPatientInWard.__id,
            ),
        );
    };

    // Discharge Patient
    const discharge = (dischargeForm) => {
        if (dispatch(hasPermission(Permissions.AGYLE_CAN_DISCHARGE))) {
            saveValues(dischargeForm, true);
        } else {
            dispatch(setSnackbar({ type: 'warning', message: t('User has no permission for discharge patient.') }));
        }
    };

    const modifyValues = (values) => {
        dispatch(modifyValuesInPatientInWard(values, currentPatientInWard, setCurrentPatientInWard));
    };

    const handleLastObs = (obs) => {
        const newObs = [
            {
                name: 'patient.lastObservation',
                value: obs,
            },
        ];

        modifyValues(newObs);
    };

    const handleCloseWarning = () => {
        dispatch(setOpenWarningFormOpen(false));
        dispatch(setTempCurrentTab(null));
    };

    const handleChooseCloseAction = () => {
        closeContextChoose.current ? closeDialog(true) : handleChangeContextTabAndCloseForm();

        if (isShowDischargeTemplates) {
            history.push('/scheduler');
            dispatch(setShowDischargeTemplates(false)); // Appointments - status change to Departed
        }
    };

    const handleChangeContextTabAndCloseForm = () => {
        dispatch(changePatientContextTab(tempCurrentTab, true));
        dispatch(clearWorkflow());
        dispatch(setFullFormsQueue([]));
        dispatch(setFullFormOpen(false));
        handleCloseWarning();
    };

    const handleGetDocumentOfRecord = (pdf) => {
        if (pdf) {
            dispatch(sendUserAudit(patient, AUDIT_TYPES.OPEN_DOCUMENT, { pdfTemplate: pdf }));

            getECS(currentPatientInWard, patientAddData, patientType, true, pdf)
                .then((res) => {
                    if (!res) {
                        dispatch(setSnackbar({ type: 'error', message: 'The document could not be generated.' }));
                    } else {
                        dispatch(setSnackbar({ type: 'success', message: 'The document was generated.' }));
                    }
                })
                .catch((e) => {
                    console.error(e);
                    dispatch(setSnackbar({ type: 'error', message: 'The document could not be generated.' }));
                });
        }
    };

    const splitScreenQueueExists = (screen) => {
        const queue = screen === 1 ? splitScreen.screenOneForms : splitScreen.screenTwoForms;
        return queue && queue.length > 0;
    };

    const canShowContext = (screen) => {
        const queue = screen === 1 ? splitScreen.screenOneForms : splitScreen.screenTwoForms;
        return _.intersection(formQueue, queue).length > 0;
    };

    const handleSplitScreenView = () => {
        const handleChangeActiveScreen = (screen) => {
            if (splitScreen?.activeScreen !== screen) {
                dispatch(
                    setContextSplitScreen({
                        activeScreen: screen,
                    }),
                );
                dispatch(setSnackbar({ type: 'info', message: t(`Screen ${screen} is active.`) }));
            }
        };

        return (
            <div className={classes.splitScreen}>
                <div
                    id="split-screen-one"
                    style={{ width: '50%', marginLeft: 1, position: 'relative' }}
                    className={clsx(classes.splitBorder, splitScreen.activeScreen === 1 && 'active')}
                    onPointerDown={() => handleChangeActiveScreen(1)}
                    ref={(ref) => (containerRef.current.one = ref)}
                >
                    {!canShowContext(1) && getContextBox(splitScreen.screenOne)}
                    {splitScreenQueueExists(1) && getFullFormContainer(1)}
                </div>

                <div
                    id="split-screen-two"
                    style={{ width: '50%', marginRight: 1, position: 'relative' }}
                    className={clsx(classes.splitBorder, splitScreen.activeScreen === 2 && 'active')}
                    onPointerDown={() => handleChangeActiveScreen(2)}
                    ref={(ref) => (containerRef.current.two = ref)}
                >
                    {!canShowContext(2) && getContextBox(splitScreen.screenTwo)}
                    {splitScreenQueueExists(2) && getFullFormContainer(2)}
                </div>
            </div>
        );
    };

    const getFullFormContainer = (screen = null) => {
        const refName = screen === 1 ? 'one' : 'two';
        return (
            <>
                <ContextFormContainer
                    patient={currentPatientInWard}
                    patientType={patientType}
                    patientAgeType={patientAgeType}
                    disableSave={isContextDisabled}
                    pastAttendances={patientIsDischarged || patientIsDischargedEdeu}
                    handleSaveData={saveValues}
                    getDoR={handleGetDocumentOfRecord}
                    screen={screen}
                    containerRef={screen ? containerRef.current?.[refName] : null}
                />

                {formQueue.map((form, index) => (
                    <FullFormContainer
                        openFormId={form}
                        key={form}
                        index={index}
                        idElement={idElement}
                        patient={currentPatientInWard}
                        patientType={patientType}
                        patientAgeType={patientAgeType}
                        disableSave={isContextDisabled}
                        pastAttendances={patientIsDischarged || patientIsDischargedEdeu}
                        handleSaveData={saveValues}
                        handleDischargePatient={discharge}
                        handleSaveAdditionalData={savePatientDataValues}
                        getDoR={handleGetDocumentOfRecord}
                        modifyLastObs={handleLastObs}
                        modifyPatient={modifyValues}
                        screen={screen}
                    />
                ))}
            </>
        );
    };

    const getContextBox = (foreTab) => {
        return (
            <ContextBox
                idElement={idElement}
                patient={currentPatientInWard}
                patientType={patientType}
                patientAgeType={patientAgeType}
                disableSave={isContextDisabled}
                pastAttendances={patientIsDischarged || patientIsDischargedEdeu}
                modyfyLastObs={handleLastObs}
                modifyValues={saveValues}
                modifyPatient={modifyValues}
                splitActive={splitScreen.enabled}
                forceTab={foreTab}
                workflowDisabled={false}
                getDoR={handleGetDocumentOfRecord}
                handleDischargePatient={discharge}
                pastAttendanceView={mainPastActivity || historyPatient}
            />
        );
    };

    const renderBackText = () => {
        if (currentTab === PATIENT_CONTEXT_TABS_ID.NOTE_DOCUMENT) {
            return 'Back';
        }
        return 'Exit patient context';
    };

    return (
        <Dialog disableEnforceFocus fullScreen open={!!contextOpen} onClose={closeDialog} disableEscapeKeyDown>
            <DialogContent
                className={clsx(
                    classes.dialogRoot,
                    (mainPastActivity || historyPatient) && classes.pastAttendanceView,
                    isLocked && classes.isLockedView,
                    isReAttendance && classes.reAttendanceView,
                )}
                style={{ height: 'auto' }}
            >
                <div id={'patientInfoRow.' + idElement}>
                    <PatientContextAlert pastAttendances={mainPastActivity || historyPatient} tags={patientObject?.tags} />

                    <AgyleHeader
                        onBack={closeDialog}
                        noProgress
                        alignItems="center"
                        backTooltip={t(renderBackText())}
                        rootStyle={classes.headerBackground}
                        disableBack={disableBack}
                    >
                        <PatientContextHeader
                            patient={currentPatientInWard}
                            patientObject={patientObject}
                            disableSave={isContextDisabled}
                            handleSaveData={saveValues}
                            pastAttendances={patientIsDischarged || patientIsDischargedEdeu}
                        />
                    </AgyleHeader>
                </div>

                <div className={classes.root}>
                    <ContextTabs
                        patient={currentPatientInWard}
                        patientType={patientType}
                        pastAttendances={patientIsDischarged || patientIsDischargedEdeu}
                        splitActive={splitScreen.enabled}
                    />

                    {splitScreen?.enabled && handleSplitScreenView()}

                    {!splitScreen?.enabled && (
                        <>
                            {!isFullFormOpen && getContextBox()}
                            {getFullFormContainer()}
                        </>
                    )}
                </div>

                <PatientContextClinicalConclusionNeeded onClose={closeDialog} />

                <MessageBox
                    title={t('Confirmation')}
                    haveAccept={{ yes: t('Yes'), no: t('No') }}
                    onAccept={closeDialog}
                    onClose={() => setVisibleCloseModal(false)}
                    message={
                        formIsOpen
                            ? t('You have not saved modifications and open form. Are you sure you want to leave without saving?')
                            : t('You have not saved modifications. Are you sure you want to leave without saving?')
                    }
                    open={!!confirmCloseModal}
                />

                {!isWarningBoxSimilarNameOpen && <PatientContextWarningDialog patient={currentPatientInWard} handleSaveData={saveValues} />}

                {similarNameWarningBox && (
                    <PatientDemographicWarning
                        open={isWarningBoxSimilarNameOpen}
                        onConfirm={closeWarningBoxSimilarName}
                        onClose={(_e, reason) => {
                            if (reason !== 'backdropClick') {
                                closeWarningBoxSimilarName();
                                closeDialog(true);
                            }
                        }}
                        patient={currentPatientInWard}
                        type="similar"
                    />
                )}

                <PatientDemographicWarning
                    open={isPatientReminderOpen}
                    onConfirm={() => {
                        tabFocus.current = false;
                        closePatientReminder();
                    }}
                    onClose={closePatientReminder}
                    patient={currentPatientInWard}
                    type="reminder"
                />

                <MessageBox
                    open={openWarningFormOpen}
                    onClose={handleCloseWarning}
                    title={t('Warning')}
                    // haveAccept={{yes: t('Close and lose data'), no: t('Stay on the form')}}
                    haveAccept={{ yes: t('Close form'), no: t('Remain on form') }}
                    onAccept={handleChooseCloseAction}
                    message={t(
                        'You currently have a form open. If you close this form, any unsaved data will be lost. Are you sure you want to continue?',
                    )}
                    warning
                />

                <BarcodeModal patient={currentPatientInWard} />

                <PatientContextReferralModal patient={currentPatientInWard} />

                <AlertUpdatePatientData />
            </DialogContent>
        </Dialog>
    );
}

export default ContextViewer;
